// driftService.js - API client for Drift Detection feature

const API_BASE_URL = 'https://driftalarm-functions-bbfmfuh5hmdsfcc7.eastus-01.azurewebsites.net/api';

/**
 * Helper to get auth token (idToken for CIAM)
 */
async function getAuthToken() {
    try {
        const msalInstance = window.msalInstance;
        if (!msalInstance) return null;

        const accounts = msalInstance.getAllAccounts();
        if (accounts.length === 0) return null;

        const request = {
            scopes: ['openid', 'profile', 'email'],
            account: accounts[0],
        };

        const response = await msalInstance.acquireTokenSilent(request);
        return response.idToken;
    } catch (error) {
        console.log('Failed to acquire token:', error.message);
        return null;
    }
}

/**
 * Helper to build headers with auth token
 */
async function getHeaders() {
    const token = await getAuthToken();
    const headers = {
        'Content-Type': 'application/json',
    };
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
}

/**
 * Get the current baseline for an asset
 * @param {string} assetId - The asset ID
 * @returns {Promise<object|null>} - Baseline data or null if not found
 */
export async function getBaseline(assetId) {
    try {
        const headers = await getHeaders();
        const response = await fetch(`${API_BASE_URL}/drift-baseline?assetId=${encodeURIComponent(assetId)}`, {
            method: 'GET',
            headers,
        });

        if (response.status === 404) return null;

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to get baseline (${response.status})`);
        }

        const data = await response.json();
        return data.baseline || data;
    } catch (error) {
        console.error('Error getting baseline:', error);
        throw error;
    }
}

/**
 * Capture a new baseline for an asset from its most recent scan
 * @param {string} assetId - The asset ID
 * @returns {Promise<object>} - Captured baseline data
 */
export async function captureBaseline(assetId) {
    try {
        const headers = await getHeaders();
        const response = await fetch(`${API_BASE_URL}/drift-baseline`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ assetId }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to capture baseline (${response.status})`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error capturing baseline:', error);
        throw error;
    }
}

/**
 * Delete the baseline for an asset
 * @param {string} assetId - The asset ID
 * @returns {Promise<object>} - Success response
 */
export async function deleteBaseline(assetId) {
    try {
        const headers = await getHeaders();
        const response = await fetch(`${API_BASE_URL}/drift-baseline?assetId=${encodeURIComponent(assetId)}`, {
            method: 'DELETE',
            headers,
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to delete baseline (${response.status})`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error deleting baseline:', error);
        throw error;
    }
}

/**
 * Adapter: map new alarm shape to legacy event shape so existing components
 * (DriftEvents.js, DriftEventsStrip.js, etc.) render without changes.
 * PRD-48 will remove this adapter when the components are rewritten.
 */
function alarmToLegacyEvent(alarm) {
    return {
        // Legacy fields expected by existing components
        eventId: alarm.alarmId,
        assetId: alarm.assetId,
        ruleId: alarm.templateId || '',
        ruleName: alarm.name || '',
        ruleSeverity: alarm.severity || 'info',
        rulePack: alarm.templateCategory || '',
        resultId: '',
        status: alarm.status === 'accepted' ? 'suppressed' :
                alarm.status === 'ignored' ? 'suppressed' :
                alarm.status || 'open',
        detail: alarm.description || alarm.name || '',
        changes: [],
        matchedItems: [],
        createdAt: alarm.firstDetectedAt || alarm.createdAt || '',
        acknowledgedAt: '',
        snoozedUntil: '',
        suppressedAt: alarm.disposition ? alarm.updatedAt : '',
        resolvedAt: alarm.resolvedAt || '',
        driftStatus: alarm.status === 'open' ? 'alert' : alarm.status,
        driftSummary: alarm.description || '',
        // New fields available from alarm model
        alarmId: alarm.alarmId,
        findingId: alarm.findingId,
        templateId: alarm.templateId,
        templateCategory: alarm.templateCategory,
        name: alarm.name,
        severity: alarm.severity,
        matchedAt: alarm.matchedAt,
        description: alarm.description,
        firstDetectedAt: alarm.firstDetectedAt,
        lastDetectedAt: alarm.lastDetectedAt,
        detectionCount: alarm.detectionCount,
    };
}

/**
 * Get drift alarms (all or for a specific asset)
 * Returns legacy event-shaped objects for backward compatibility.
 * @param {string} [assetId] - Optional asset ID to filter by
 * @returns {Promise<Array>} - Array of drift events (alarm-backed)
 */
export async function getEvents(assetId) {
    try {
        const headers = await getHeaders();
        const url = assetId
            ? `${API_BASE_URL}/drift-alarms/${encodeURIComponent(assetId)}`
            : `${API_BASE_URL}/drift-alarms`;
        const response = await fetch(url, { method: 'GET', headers });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to get alarms (${response.status})`);
        }

        const data = await response.json();
        const alarms = data.alarms || [];
        return alarms.map(alarmToLegacyEvent);
    } catch (error) {
        console.error('Error getting drift alarms:', error);
        return [];
    }
}

// Backward compat alias
export const getDriftEvents = getEvents;

// ── PRD-48: Native alarm API (no legacy adapter) ──

/**
 * Get drift alarms in native alarm shape (PRD-48 queue page).
 * @param {object} [opts] - { assetId, includeDispositioned, severity }
 * @returns {Promise<Array>} - Array of alarm objects
 */
export async function getAlarms(opts = {}) {
    const headers = await getHeaders();
    const params = new URLSearchParams();
    if (opts.includeDispositioned) params.set('includeDispositioned', 'true');
    if (opts.severity && opts.severity !== 'all') params.set('severity', opts.severity);
    if (opts.status && opts.status !== 'all') params.set('status', opts.status);
    if (opts.assetId) params.set('assetId', opts.assetId);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const url = `${API_BASE_URL}/drift-alarms${qs}`;
    const response = await fetch(url, { method: 'GET', headers });
    if (!response.ok) {
        // Surface auth failures as a recognizable session-expired error so the
        // page can prompt re-auth instead of rendering an empty-state lie.
        if (response.status === 401) {
            throw new Error('Your session has expired — please refresh the page or sign in again.');
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to load alarms (${response.status})`);
    }
    const data = await response.json();
    return data.alarms || [];
}

/**
 * PRD-130: Get the CTEM "fix these first" prioritization for the account
 * (or one asset). Returns the full payload (topThree/ranked/suggestions/…),
 * NOT just an alarm array — the widget needs the scored factors + suggestions.
 * @param {object} [opts] - { assetId }
 * @returns {Promise<object>} - { success, scope, assetCount, alarmCount, ranked, topThree, suggestions, computedAt, kevEpssCacheStatus, degraded }
 */
export async function getAlarmPrioritizer(opts = {}) {
    const headers = await getHeaders();
    const params = new URLSearchParams();
    if (opts.assetId) params.set('assetId', opts.assetId);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const url = `${API_BASE_URL}/alarm-prioritizer${qs}`;
    const response = await fetch(url, { method: 'GET', headers });
    if (!response.ok) {
        if (response.status === 401) {
            throw new Error('Your session has expired — please refresh the page or sign in again.');
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to load prioritization (${response.status})`);
    }
    return await response.json();
}

/**
 * Get a single alarm detail by alarmId.
 * @param {string} alarmId
 * @returns {Promise<object>} - Alarm object
 */
export async function getAlarmDetail(alarmId) {
    const headers = await getHeaders();
    const response = await fetch(`${API_BASE_URL}/drift-alarms/${encodeURIComponent(alarmId)}`, {
        method: 'GET', headers,
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to get alarm (${response.status})`);
    }
    const data = await response.json();
    return data.alarm || data;
}

/**
 * Accept an alarm — known risk, stays in score.
 * @param {string} alarmId
 * @param {object} body - { reason, expiresInDays, scope }
 */
export async function acceptAlarm(alarmId, body) {
    const headers = await getHeaders();
    const response = await fetch(`${API_BASE_URL}/drift-alarms/${encodeURIComponent(alarmId)}/accept`, {
        method: 'PUT', headers,
        body: JSON.stringify(body),
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to accept alarm');
    }
    return await response.json();
}

/**
 * Ignore an alarm — false positive, removed from score.
 * @param {string} alarmId
 * @param {object} body - { reason, expiresAt, scope }
 */
export async function ignoreAlarm(alarmId, body) {
    const headers = await getHeaders();
    const response = await fetch(`${API_BASE_URL}/drift-alarms/${encodeURIComponent(alarmId)}/ignore`, {
        method: 'PUT', headers,
        body: JSON.stringify(body),
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to ignore alarm');
    }
    return await response.json();
}

/**
 * Get finding detail for alarm drawer HTTP transaction view.
 * @param {string} assetId
 * @param {string} findingId
 */
export async function getFindingDetail(assetId, findingId) {
    const headers = await getHeaders();
    const response = await fetch(
        `${API_BASE_URL}/finding-detail/${encodeURIComponent(assetId)}/${encodeURIComponent(findingId)}`,
        { method: 'GET', headers }
    );
    if (!response.ok) return null;
    const data = await response.json();
    return data.finding || null;
}

/**
 * Request AI remediation for an alarm.
 * @param {string} alarmId
 * @param {boolean} [forceRefresh]
 */
export async function getAlarmRemediation(alarmId, forceRefresh = false) {
    const headers = await getHeaders();
    const response = await fetch(`${API_BASE_URL}/drift-alarms/${encodeURIComponent(alarmId)}/remediation`, {
        method: 'POST', headers,
        body: JSON.stringify({ forceRefresh }),
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to get remediation');
    }
    return await response.json();
}

// ── PRD-49: Bulk disposition API ──

/**
 * Bulk accept or ignore multiple alarms at once.
 * PRD-63 WP1: extended with baselineAcceptance + bulkBatchId for chunked
 * baseline accept. When baselineAcceptance is present the API enforces
 * locked field values (reason / expiresInDays / scope) and writes
 * baseline-acceptance metadata to the asset entity on the final chunk.
 * @param {string[]} alarmIds - Array of alarm IDs
 * @param {string} disposition - 'accept' or 'ignore'
 * @param {object} opts - { expiresInDays, expiresAt, scope, reason, baselineAcceptance, bulkBatchId }
 */
export async function bulkDisposition(alarmIds, disposition, opts = {}) {
    const headers = await getHeaders();
    const body = {
        alarmIds,
        disposition,
        expiresInDays: opts.expiresInDays,
        expiresAt: opts.expiresAt,
        scope: opts.scope || 'host',
        reason: opts.reason,
    };
    if (opts.baselineAcceptance) body.baselineAcceptance = opts.baselineAcceptance;
    if (opts.bulkBatchId) body.bulkBatchId = opts.bulkBatchId;

    const response = await fetch(`${API_BASE_URL}/drift-alarms-bulk-disposition`, {
        method: 'POST', headers,
        body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        // Surface API status + body so callers can branch on 409 (already
        // baselined / two-tab race) without re-fetching.
        const err = new Error(data.error || `Bulk disposition failed (${response.status})`);
        err.status = response.status;
        err.body = data;
        throw err;
    }
    return data;
}

// PRD-63 WP1: baseline-accept resumable chunking. SPA persists TWO
// localStorage entries keyed by assetId so a partial run can resume the
// same bulkBatchId AND surface the correct cumulative count to the API's
// final-chunk asset entity write. Without both keys a 1200-alarm baseline
// that fails after chunk 2 would leave asset.baselineAcceptedCount=200
// while suppressions returns 1200 — auditor sees two numbers for one event.
const LS_BATCH_KEY = (assetId) => `daBaselineBatchInProgress:${assetId}`;
const LS_TOTAL_KEY = (assetId) => `daBaselineTotal:${assetId}`;
const BASELINE_CHUNK_SIZE = 500;

/**
 * Accept all of an asset's alarms as the initial baseline. Handles
 * chunking, resume-after-network-failure, and the 409 already-accepted path.
 *
 * @param {string} assetId
 * @param {string[]} alarmIds - Full list of open alarms for the asset
 * @returns {Promise<{ bulkBatchId: string, totalSucceeded: number }>}
 */
export async function acceptAllAsBaseline(assetId, alarmIds) {
    if (!assetId) throw new Error('assetId is required');
    if (!Array.isArray(alarmIds) || alarmIds.length === 0) {
        throw new Error('No alarms to accept');
    }

    let bulkBatchId = localStorage.getItem(LS_BATCH_KEY(assetId)) || undefined;
    let totalSucceeded = parseInt(localStorage.getItem(LS_TOTAL_KEY(assetId)) || '0', 10);

    try {
        for (let i = 0; i < alarmIds.length; i += BASELINE_CHUNK_SIZE) {
            const chunk = alarmIds.slice(i, i + BASELINE_CHUNK_SIZE);
            const isFinalChunk = i + BASELINE_CHUNK_SIZE >= alarmIds.length;
            const projectedTotal = totalSucceeded + chunk.length;

            const result = await bulkDisposition(chunk, 'accept', {
                expiresInDays: 365,
                reason: 'Initial baseline acceptance',
                scope: 'asset',
                baselineAcceptance: {
                    assetId,
                    isFinalChunk,
                    totalSucceeded: projectedTotal,
                },
                bulkBatchId,
            });

            if (!bulkBatchId) {
                bulkBatchId = result.bulkBatchId;
                localStorage.setItem(LS_BATCH_KEY(assetId), bulkBatchId);
            }
            totalSucceeded += (result.succeeded || 0);
            localStorage.setItem(LS_TOTAL_KEY(assetId), String(totalSucceeded));
        }
    } catch (err) {
        // 409 = already baselined (sequential or two-tab race). Clear
        // resume markers so the next attempt doesn't try to continue a
        // batch the server considers closed.
        if (err.status === 409) {
            localStorage.removeItem(LS_BATCH_KEY(assetId));
            localStorage.removeItem(LS_TOTAL_KEY(assetId));
        }
        throw err;
    }

    localStorage.removeItem(LS_BATCH_KEY(assetId));
    localStorage.removeItem(LS_TOTAL_KEY(assetId));
    return { bulkBatchId, totalSucceeded };
}

/**
 * Dismiss the "Accept All as Initial Baseline" banner for an asset.
 * Permanent — banner does not return for this asset.
 */
export async function dismissBaselineBanner(assetId) {
    const headers = await getHeaders();
    const response = await fetch(`${API_BASE_URL}/asset-baseline-dismiss/${encodeURIComponent(assetId)}`, {
        method: 'POST', headers,
        body: JSON.stringify({}),
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to dismiss baseline banner');
    }
    return await response.json();
}

/**
 * Get just alarm IDs matching filters (for select-all-matching).
 * @param {object} opts - { assetId, severity, includeDispositioned }
 * @returns {Promise<{alarmIds: string[], total: number, truncated: boolean}>}
 */
export async function getAlarmIds(opts = {}) {
    const headers = await getHeaders();
    const params = new URLSearchParams({ onlyIds: 'true' });
    if (opts.includeDispositioned) params.set('includeDispositioned', 'true');
    if (opts.severity && opts.severity !== 'all') params.set('severity', opts.severity);
    if (opts.status && opts.status !== 'all') params.set('status', opts.status);
    if (opts.assetId) params.set('assetId', opts.assetId);
    const url = `${API_BASE_URL}/drift-alarms?${params}`;
    const response = await fetch(url, { method: 'GET', headers });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to get alarm IDs');
    }
    return await response.json();
}

// Legacy compat — kept for DriftEventsStrip and DriftEventsList consumers
export const acknowledgeEvent = acceptAlarm;
export const suppressEvent = ignoreAlarm;
export async function snoozeEvent() { throw new Error('Snooze removed in PRD-46. Use Accept with expiry.'); }
export async function resolveEvent() { throw new Error('Manual resolve removed in PRD-46. Alarms resolve automatically.'); }

// ── Drift Rules API ──

/**
 * Get all drift rules for the current user
 */
export async function getRules() {
    const headers = await getHeaders();
    const response = await fetch(`${API_BASE_URL}/drift-rules`, { method: 'GET', headers });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to get rules');
    }
    const data = await response.json();
    return data.rules || [];
}

/**
 * Create a custom drift rule (Pro+)
 */
export async function createRule(ruleData) {
    const headers = await getHeaders();
    const response = await fetch(`${API_BASE_URL}/drift-rules`, {
        method: 'POST', headers,
        body: JSON.stringify(ruleData),
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create rule');
    }
    return await response.json();
}

/**
 * Update a drift rule
 */
export async function updateRule(ruleId, updates) {
    const headers = await getHeaders();
    const response = await fetch(`${API_BASE_URL}/drift-rules/${encodeURIComponent(ruleId)}`, {
        method: 'PUT', headers,
        body: JSON.stringify(updates),
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update rule');
    }
    return await response.json();
}

/**
 * Delete a custom drift rule
 */
export async function deleteRule(ruleId) {
    const headers = await getHeaders();
    const response = await fetch(`${API_BASE_URL}/drift-rules/${encodeURIComponent(ruleId)}`, {
        method: 'DELETE', headers,
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to delete rule');
    }
    return await response.json();
}

/**
 * Initialize default rules for the current user
 */
export async function initializeRules() {
    const headers = await getHeaders();
    const response = await fetch(`${API_BASE_URL}/drift-initialize`, {
        method: 'POST', headers,
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to initialize rules');
    }
    return await response.json();
}

// ── Notification Channels API ──

/**
 * Get all notification channels for the current user
 */
export async function getNotificationChannels() {
    try {
        const headers = await getHeaders();
        const response = await fetch(`${API_BASE_URL}/notification-channels`, { method: 'GET', headers });
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || 'Failed to get channels');
        }
        const data = await response.json();
        return data.channels || [];
    } catch (error) {
        console.error('Error getting notification channels:', error);
        return [];
    }
}

/**
 * Create a notification channel
 */
export async function createNotificationChannel(channelData) {
    const headers = await getHeaders();
    const response = await fetch(`${API_BASE_URL}/notification-channels`, {
        method: 'POST', headers,
        body: JSON.stringify(channelData),
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create channel');
    }
    return await response.json();
}

/**
 * Update a notification channel
 */
export async function updateNotificationChannel(channelId, updates) {
    const headers = await getHeaders();
    const response = await fetch(`${API_BASE_URL}/notification-channels/${encodeURIComponent(channelId)}`, {
        method: 'PUT', headers,
        body: JSON.stringify(updates),
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update channel');
    }
    return await response.json();
}

/**
 * Delete a notification channel
 */
export async function deleteNotificationChannel(channelId) {
    const headers = await getHeaders();
    const response = await fetch(`${API_BASE_URL}/notification-channels/${encodeURIComponent(channelId)}`, {
        method: 'DELETE', headers,
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to delete channel');
    }
    return await response.json();
}

/**
 * Test a notification channel
 */
export async function testNotificationChannel(channelId) {
    const headers = await getHeaders();
    const response = await fetch(`${API_BASE_URL}/notification-channels-test/${encodeURIComponent(channelId)}`, {
        method: 'POST', headers,
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to test channel');
    }
    return await response.json();
}

/**
 * Export drift events as CSV or JSON
 * @param {string} format - 'csv' or 'json'
 * @param {string} [startDate] - ISO date start filter
 * @param {string} [endDate] - ISO date end filter
 * @returns {Promise<Blob>} - File blob for download
 */
export async function getEventsExport(format, startDate, endDate) {
    const headers = await getHeaders();
    const params = new URLSearchParams({ format });
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await fetch(`${API_BASE_URL}/drift-alarms-export?${params}`, {
        method: 'GET',
        headers,
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to export events');
    }
    return await response.blob();
}

/**
 * Get drift summary data for all assets
 * Computes summary from asset list data (drift statuses are on asset entities)
 * @param {Array} assets - Array of asset objects with driftStatus field
 * @returns {object} - Summary with counts and recent changes
 */
export function getDriftSummary(assets) {
    const summary = {
        total: assets.length,
        stable: 0,
        changed: 0,
        alert: 0,
        unscanned: 0,
        recentChanges: []
    };

    for (const asset of assets) {
        const status = asset.driftStatus || 'unscanned';
        if (summary.hasOwnProperty(status)) {
            summary[status]++;
        } else {
            summary.unscanned++;
        }
    }

    return summary;
}

// ===== API Key Management =====

export async function getApiKeys() {
    const headers = await getHeaders();
    const response = await fetch(`${API_BASE_URL}/api-keys`, { headers });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to fetch API keys');
    }
    return response.json();
}

export async function createApiKey(data) {
    const headers = await getHeaders();
    headers['Content-Type'] = 'application/json';
    const response = await fetch(`${API_BASE_URL}/api-keys`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to create API key');
    }
    return response.json();
}

export async function updateApiKey(keyId, data) {
    const headers = await getHeaders();
    headers['Content-Type'] = 'application/json';
    const response = await fetch(`${API_BASE_URL}/api-keys/${keyId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data)
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to update API key');
    }
    return response.json();
}

export async function revokeApiKey(keyId) {
    const headers = await getHeaders();
    const response = await fetch(`${API_BASE_URL}/api-keys/${keyId}`, {
        method: 'DELETE',
        headers
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to revoke API key');
    }
    return response.json();
}
